// Store de torneios — lista, registra, cria (admin).
//
// Convencao: load() refaz fetch on demand (lobby/aba aberta + apos
// register/unregister). Sem polling — atualizacoes vivas serao via WS
// quando torneio comecar (proxima fase).

import { writable, derived, get } from 'svelte/store';
import { api } from '../lib/api';
import type { ApiError } from '../lib/api';
import { userId } from './auth';
import { matchSnapshot } from './match';
import { showAlert } from './alert';

export type TournamentStatus = 'scheduled' | 'running' | 'finished' | 'cancelled';

export type BracketMatch = {
  id: string;
  team_a: string[];
  team_b: string[];
  match_id?: string;
  winner?: string;
};

export type Round = {
  index: number;
  matches: BracketMatch[];
};

export type Bracket = {
  rounds: Round[];
};

export type PrizeShare = {
  position: number;
  pct: number;
};

export type Tournament = {
  id: string;
  modality: string; // 'paulista' | 'mineiro'
  format: '1v1' | '2v2';
  size: number;
  name?: string;
  buy_in_cents: number;
  prize_structure?: PrizeShare[];
  starts_at: string; // ISO
  status: TournamentStatus;
  registrations?: string[];
  bracket?: Bracket;
  champion?: string;
  // player_names: pid -> nick display. Cobre humanos (lookup do Nickname
  // no UserRepo) E bots (RandomName). Populado em Start(); imutavel
  // durante torneio. `bot_names` foi renomeado pra `player_names` em
  // 2026-05-25; manter alias por compat retro.
  player_names?: Record<string, string>;
  bot_names?: Record<string, string>; // legacy — apenas leitura de torneios pre-fix
  created_at: string;
  updated_at: string;
  created_by?: string;
  // recurrence_interval_minutes > 0 marca o doc como TEMPLATE gerador
  // (nao e um torneio jogavel — nunca inicia, so cospe filhos). Front
  // deve esconder templates da lista inscritivel.
  recurrence_interval_minutes?: number;
  recurrence_parent_id?: string;
};

export const tournaments = writable<Tournament[]>([]);
export const tournamentsLoading = writable(false);
export const tournamentsError = writable<string | null>(null);

export async function loadTournaments(): Promise<void> {
  tournamentsLoading.set(true);
  tournamentsError.set(null);
  try {
    const data = await api.get<{ tournaments: Tournament[] }>('/tournaments');
    tournaments.set(data.tournaments ?? []);
  } catch (e) {
    const err = e as ApiError | Error;
    tournamentsError.set(err.message ?? 'erro ao carregar torneios');
  } finally {
    tournamentsLoading.set(false);
  }
}

// loadAllTournaments puxa todos (scheduled + running + finished + cancelled)
// pro historico publico. Substitui o tournaments store com a lista completa.
export async function loadAllTournaments(): Promise<void> {
  tournamentsLoading.set(true);
  tournamentsError.set(null);
  try {
    const data = await api.get<{ tournaments: Tournament[] }>('/tournaments/all');
    tournaments.set(data.tournaments ?? []);
  } catch (e) {
    const err = e as ApiError | Error;
    tournamentsError.set(err.message ?? 'erro ao carregar historico');
  } finally {
    tournamentsLoading.set(false);
  }
}

// isTournamentGone — true se o erro indica que o torneio nao existe mais
// (404 do backend OU mensagem "not found"). Acontece quando o cron deleta
// um recorrente vazio entre o refresh da lista e o clique do usuario.
function isTournamentGone(err: ApiError | Error): boolean {
  if ((err as ApiError).status === 404) return true;
  const msg = (err.message ?? '').toLowerCase();
  return msg.includes('not found') || msg.includes('nao encontrado') || msg.includes('não encontrado');
}

// dropTournament — remove o torneio da lista local na hora e resincroniza
// com o backend. Garante que um torneio inexistente "nunca aparece":
// some imediatamente no clique + a lista volta consistente no proximo load.
function dropTournament(id: string): void {
  tournaments.update((list) => list.filter((x) => x.id !== id));
  void loadAllTournaments();
}

export async function registerTournament(id: string): Promise<Tournament | null> {
  try {
    const t = await api.post<Tournament>(`/tournaments/${id}/register`);
    // Atualiza lista local
    tournaments.update((list) => list.map((x) => (x.id === id ? t : x)));
    return t;
  } catch (e) {
    const err = e as ApiError | Error;
    if (isTournamentGone(err)) {
      // Torneio sumiu (cron deletou recorrente vazio). Tira da tela e
      // resync — nunca deve continuar clicavel. Felipe 2026-06-13.
      dropTournament(id);
      showAlert('Esse torneio já encerrou. Atualizando a lista...', { tone: 'warn' });
      return null;
    }
    // Popup amigavel em vez de faixa de erro feia (Felipe 2026-05-31).
    showAlert(friendlyTournamentError(err.message), { tone: 'warn' });
    return null;
  }
}

export async function unregisterTournament(id: string): Promise<Tournament | null> {
  try {
    const t = await api.delete<Tournament>(`/tournaments/${id}/register`);
    tournaments.update((list) => list.map((x) => (x.id === id ? t : x)));
    return t;
  } catch (e) {
    const err = e as ApiError | Error;
    if (isTournamentGone(err)) {
      dropTournament(id);
      showAlert('Esse torneio já encerrou. Atualizando a lista...', { tone: 'warn' });
      return null;
    }
    showAlert(friendlyTournamentError(err.message), { tone: 'warn' });
    return null;
  }
}

// friendlyTournamentError traduz mensagens cruas do backend (com prefixo
// "tournament:" e jargao) em texto humano pro popup.
function friendlyTournamentError(raw?: string): string {
  const msg = raw ?? '';
  if (msg.includes('ja inscrito em outro torneio')) {
    return 'Você já está inscrito em outro torneio. Termine ou cancele aquele antes de entrar neste.';
  }
  if (msg.includes('full') || msg.includes('lotado') || msg.includes('cheio')) {
    return 'Este torneio já está cheio. Tente o próximo!';
  }
  if (msg.includes('already started') || msg.includes('já começou')) {
    return 'Este torneio já começou — as inscrições foram encerradas.';
  }
  if (msg.includes('already registered')) {
    return 'Você já está inscrito neste torneio.';
  }
  // Fallback: limpa o prefixo tecnico "tournament: "
  return msg.replace(/^tournament:\s*/i, '') || 'Não foi possível concluir. Tente novamente.';
}

export type CreateTournamentInput = {
  modality?: string;
  format?: '1v1' | '2v2';
  size: number;
  name?: string;
  starts_at: string; // ISO
  buy_in_cents?: number;
  prize_structure?: PrizeShare[];
};

export async function createTournament(input: CreateTournamentInput): Promise<Tournament | null> {
  try {
    const t = await api.post<Tournament>('/admin/tournaments', input);
    // Adiciona à lista local
    tournaments.update((list) => [t, ...list]);
    return t;
  } catch (e) {
    const err = e as ApiError | Error;
    tournamentsError.set(err.message ?? 'erro ao criar torneio');
    return null;
  }
}

// Start admin: dispara o torneio AGORA (cria matches da round 0 e
// avanca status -> running). Backend valida is_admin via middleware.
export async function startTournament(id: string): Promise<Tournament | null> {
  try {
    const t = await api.post<Tournament>(`/admin/tournaments/${id}/start`);
    tournaments.update((list) => list.map((x) => (x.id === id ? t : x)));
    return t;
  } catch (e) {
    const err = e as ApiError | Error;
    tournamentsError.set(err.message ?? 'erro ao iniciar torneio');
    return null;
  }
}

// Helper: sou admin? Setado em login/register OU por loadIsAdmin abaixo.
export const isAdmin = writable(false);

// Lê /users/me e atualiza o flag isAdmin + score do user. Útil no mount
// do componente pra reload (quando Firebase restaura session sem passar
// pelo login) e pra refresh do score apos partidas (chamado em $effect).
export async function loadIsAdmin(): Promise<void> {
  try {
    const me = await api.get<{ is_admin?: boolean; score?: number }>('/users/me');
    isAdmin.set(!!me.is_admin);
    const { userScore } = await import('./auth');
    userScore.set(me.score ?? 1500);
  } catch {
    isAdmin.set(false);
  }
}

// Conveniencia: torneios futuros ordenados por starts_at. Mostra so os
// PROXIMOS 5 (Leonardo 2026-06-01: "so 5 na fila") — o backend gera
// varios pra encher a janela, mas a UI exibe so os 5 mais proximos.
// Esconde tambem os que JA PASSARAM (starts_at no passado alem de uma
// folga curta) — Leonardo: "tem torneio que ja passou e ainda aparece".
const UPCOMING_LIMIT = 5;
const PAST_GRACE_MS = 90_000; // 90s de folga (cron leva ate ~1min pra iniciar)
export const upcomingTournaments = derived(tournaments, ($tournaments) => {
  const cutoff = Date.now() - PAST_GRACE_MS;
  return [...$tournaments]
    .filter(
      (t) =>
        t.status === 'scheduled' &&
        !(t.recurrence_interval_minutes && t.recurrence_interval_minutes > 0) &&
        new Date(t.starts_at).getTime() > cutoff,
    )
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .slice(0, UPCOMING_LIMIT);
});

// Aux: dado um pid, retorna se ele pertence a um time `team`.
function pidInTeam(pid: string, team?: string[]): boolean {
  return !!team && team.includes(pid);
}

// waitingForNextRound — quando o jogador venceu sua match atual mas a
// próxima ainda não foi spawnada (sibling match ainda jogando ou
// aguardando spawn). Retorna { tournament, roundIdx, matchIdx } pra
// renderizar a tela <WaitingNextOpponent>, ou null se não tá em waiting.
//
// Lógica:
//   1. Acha torneios running em que o pid está inscrito
//   2. Pra cada um, encontra a "última match" do pid: aquela com winner
//      em que o pid faz parte do time vencedor
//   3. Se essa match NÃO é a última round e a próxima match dele
//      (matches[matchIdx/2] na próxima round) ainda tem team_a/team_b
//      vazio OU sem match_id → está aguardando.
export const waitingForNextRound = derived(
  [tournaments, userId],
  ([$tournaments, $userId]) => {
    if (!$userId) return null;
    for (const t of $tournaments) {
      if (t.status !== 'running' || !t.bracket?.rounds?.length) continue;
      // Encontra última match em que o user venceu
      let myLastWonRound = -1;
      let myLastWonIdx = -1;
      for (let ri = 0; ri < t.bracket.rounds.length; ri++) {
        const matches = t.bracket.rounds[ri].matches;
        for (let mi = 0; mi < matches.length; mi++) {
          const bm = matches[mi];
          if (!bm.winner) continue;
          const winnerTeam = bm.winner === 'A' ? bm.team_a : bm.team_b;
          if (pidInTeam($userId, winnerTeam)) {
            myLastWonRound = ri;
            myLastWonIdx = mi;
          }
        }
      }
      if (myLastWonRound < 0) continue;
      // É a última round (final)? Então venceu o torneio. Não está em waiting.
      if (myLastWonRound === t.bracket.rounds.length - 1) continue;
      // Próxima match correspondente
      const nextRoundIdx = myLastWonRound + 1;
      const nextMatchIdx = Math.floor(myLastWonIdx / 2);
      const nextBM = t.bracket.rounds[nextRoundIdx]?.matches?.[nextMatchIdx];
      // Se a próxima match já foi spawnada (tem match_id), o WS já mandou
      // game:state e o jogador caiu na mesa. Aqui o derived só responde
      // "true" enquanto ainda não tem match_id.
      if (nextBM && !nextBM.match_id) {
        return { tournament: t, roundIdx: myLastWonRound, matchIdx: myLastWonIdx };
      }
    }
    return null;
  },
);

// inActiveTournamentMatch — player está em uma match ativa (com match_id)
// dentro de um torneio. Usado pelo front pra desabilitar single-matches
// enquanto torneio está rolando.
export const inActiveTournamentMatch = derived(
  [tournaments, userId],
  ([$tournaments, $userId]) => {
    if (!$userId) return false;
    for (const t of $tournaments) {
      if (t.status !== 'running' || !t.bracket?.rounds?.length) continue;
      for (const round of t.bracket.rounds) {
        for (const bm of round.matches) {
          if (!bm.match_id || bm.winner) continue;
          if (pidInTeam($userId, bm.team_a) || pidInTeam($userId, bm.team_b)) {
            return true;
          }
        }
      }
    }
    return false;
  },
);

// bracketModalOpen — controla a abertura do modal de chaveamento durante
// a partida. Setado por componentes profundos (Scoreboard2v2) e ouvido
// pelo App.svelte que renderiza o modal. Evita prop drilling.
export const bracketModalOpen = writable(false);

// bracketTournament — torneio a exibir no modal de chaveamento quando
// NAO ha match ativa (ex: tela de espera entre fases). Em partida, o
// App usa currentMatchTournament; aqui e o fallback explicito.
export const bracketTournament = writable<Tournament | null>(null);

// championCelebration — torneio que o jogador acabou de VENCER (campeao).
// Setado pelo handler tournament:state quando status=finished e o user
// faz parte do time campeao. App mostra ChampionModal. Limpa ao fechar.
export const championCelebration = writable<Tournament | null>(null);

// isMyChampionTournament — o user faz parte do time campeao desse torneio?
// Em 2v2 checa o time vencedor da final (nao so o leader t.champion).
export function isMyChampionTournament(t: Tournament, uid: string): boolean {
  if (!uid || t.status !== 'finished' || !t.bracket?.rounds?.length) return false;
  const finalRound = t.bracket.rounds[t.bracket.rounds.length - 1];
  const finalMatch = finalRound?.matches?.[0];
  if (!finalMatch || !finalMatch.winner) return false;
  const champTeam = finalMatch.winner === 'A' ? finalMatch.team_a : finalMatch.team_b;
  return pidInTeam(uid, champTeam);
}

// currentMatchTournament — reativo: retorna o Tournament em que a Match
// atual ($matchSnapshot) faz parte, ou null se Match avulsa.
// Atualiza automaticamente quando matchSnapshot ou tournaments mudam.
export const currentMatchTournament = derived(
  [matchSnapshot, tournaments],
  ([$snap, $tournaments]) => {
    if (!$snap?.id) return null;
    for (const t of $tournaments) {
      if (!t.bracket?.rounds?.length) continue;
      for (const round of t.bracket.rounds) {
        for (const bm of round.matches) {
          if (bm.match_id === $snap.id) return t;
        }
      }
    }
    return null;
  },
);

// Torneios em andamento (admin/qualquer player ve fase atual + bracket).
export const runningTournaments = derived(tournaments, ($tournaments) =>
  [...$tournaments]
    .filter((t) => t.status === 'running')
    .sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()),
);

// Torneios terminados — historico. Mais recentes primeiro.
export const finishedTournaments = derived(tournaments, ($tournaments) =>
  [...$tournaments]
    .filter((t) => t.status === 'finished' || t.status === 'cancelled')
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
);

// Historico SO do player atual (Leonardo 2026-05-31: tira historico
// publico, manter so o do player). Lista separada da `tournaments`
// pra nao misturar com upcoming/running do lobby.
export const myHistory = writable<Tournament[]>([]);
export const myHistoryLoading = writable(false);

export async function loadMyHistory(): Promise<void> {
  myHistoryLoading.set(true);
  try {
    const data = await api.get<{ tournaments: Tournament[] }>('/tournaments/history/mine');
    myHistory.set(data.tournaments ?? []);
  } catch (e) {
    const err = e as ApiError | Error;
    tournamentsError.set(err.message ?? 'erro ao carregar historico');
  } finally {
    myHistoryLoading.set(false);
  }
}
